import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getDevices, banDevice, unbanDevice } from "../../../data";
import { AdminLayout } from "../shared/layout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "@/components/ui/use-toast";
import { Smartphone, Ban, CheckCircle } from "lucide-react";
import { format } from "date-fns";
import { tr } from "date-fns/locale";

export function DevicesScreen() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["devices"],
    queryFn: getDevices,
  });

  const banMutation = useMutation({
    mutationFn: ({ id, reason }) => banDevice(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries(["devices"]);
      toast({
        title: "Başarılı",
        description: "Cihaz yasaklandı",
        variant: "success",
      });
    },
  });

  const unbanMutation = useMutation({
    mutationFn: unbanDevice,
    onSuccess: () => {
      queryClient.invalidateQueries(["devices"]);
      toast({
        title: "Başarılı",
        description: "Yasak kaldırıldı",
        variant: "success",
      });
    },
  });

  const handleBan = (device) => {
    const reason = prompt("Ban sebebi:");
    if (reason) {
      banMutation.mutate({ id: device.id, reason });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return format(new Date(dateString), "dd MMM yyyy, HH:mm", { locale: tr });
    } catch (error) {
      return dateString;
    }
  };

  const devices = data?.data?.data || [];

  return (
    <AdminLayout
      title="Devices"
      description="Kullanıcı cihazlarını yönetin"
      loading={isLoading}
    >
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Smartphone className="mr-2 h-5 w-5" />
            Devices ({devices.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {devices.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Smartphone className="mx-auto h-12 w-12 mb-4 text-gray-300" />
              <p>Henüz cihaz kaydı bulunmuyor.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Cihaz</TableHead>
                  <TableHead>Kullanıcı</TableHead>
                  <TableHead>Platform</TableHead>
                  <TableHead>Son Aktif</TableHead>
                  <TableHead>Durum</TableHead>
                  <TableHead>İşlemler</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {devices.map((device) => (
                  <TableRow key={device.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{device.deviceName || "Unknown"}</p>
                        <p className="text-xs text-gray-500">{device.deviceId}</p>
                      </div>
                    </TableCell>
                    <TableCell>{device.user?.email || "N/A"}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{device.platform?.toUpperCase()}</Badge>
                    </TableCell>
                    <TableCell>{formatDate(device.lastActiveAt)}</TableCell>
                    <TableCell>
                      {device.ban ? (
                        <Badge variant="destructive">
                          <Ban className="mr-1 h-3 w-3" />
                          Yasaklı
                        </Badge>
                      ) : (
                        <Badge variant="success">
                          <CheckCircle className="mr-1 h-3 w-3" />
                          Aktif
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {device.ban ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => unbanMutation.mutate(device.id)}
                          disabled={unbanMutation.isLoading}
                        >
                          Yasağı Kaldır
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleBan(device)}
                          disabled={banMutation.isLoading}
                          className="text-red-500"
                        >
                          Yasakla
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </AdminLayout>
  );
}

